/**
 * Someone opens an app link (a report in an email, /workspace, /new-project)
 * while signed out. Instead of dropping them on the homepage, we send them to
 * the login page and, once signed in, back to the page they asked for.
 *
 * sessionStorage: this tab only, gone when it closes. Necessary for the sign-in
 * the visitor asked for; listed in cookieLibrary.ts. Expires after one hour and
 * is removed as soon as it is used. Only same-site app paths are accepted.
 */
export const AUTH_RETURN_KEY = "ec_auth_return";
const TTL_MS = 3600_000;

const safePath = (p: unknown): p is string =>
  typeof p === "string" && p.startsWith("/") && !p.startsWith("//") && !/^\/(login|signup|onboarding|auth)\b/.test(p) && p.length < 500;

export function rememberAuthReturn(path: string): void {
  if (!safePath(path) || path === "/" || path === "/workspace") return;
  try {
    sessionStorage.setItem(AUTH_RETURN_KEY, JSON.stringify({ path, at: Date.now() }));
  } catch {
    /* storage blocked: they land on the workspace instead */
  }
}

/** The page to go back to after signing in, once; null when nothing is waiting. */
export function takeAuthReturn(): string | null {
  try {
    const raw = sessionStorage.getItem(AUTH_RETURN_KEY);
    if (!raw) return null;
    sessionStorage.removeItem(AUTH_RETURN_KEY);
    const v = JSON.parse(raw) as { path?: unknown; at?: unknown };
    if (!safePath(v.path)) return null;
    if (typeof v.at !== "number" || Date.now() - v.at > TTL_MS) return null;
    return v.path;
  } catch {
    return null;
  }
}
